import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Producto } from '@tanteo/compartido';
import { fechaNegocio, formatearCOP, formatearFecha } from '@tanteo/compartido';
import { listarProductos } from '../../lib/api';
import { useApp } from '../../lib/contexto';
import { Boton, Esqueleto } from '../../ui/base';
import { CodigoBarras } from './CodigoBarras';
import { instalacion } from '@tanteo/compartido/instalacion';

/**
 * Carta de códigos imprimible (docs/02-ux.md §7, ADR-0006): grid por categoría,
 * Code128 + nombre + precio + SKU en texto. El código codifica el SKU, no el
 * precio: subir precios no invalida cartas impresas.
 */
export function CartaCodigos() {
  const { negocio } = useApp();
  const [productos, setProductos] = useState<Producto[] | null>(null);

  useEffect(() => {
    if (!negocio) return;
    listarProductos(negocio.negocioId)
      .then((ps) => setProductos(ps.filter((p) => p.activo)))
      .catch(() => setProductos([]));
  }, [negocio]);

  const porCategoria = useMemo(() => {
    const grupos = new Map<string, Producto[]>();
    for (const p of productos ?? []) {
      const lista = grupos.get(p.categoria) ?? [];
      lista.push(p);
      grupos.set(p.categoria, lista);
    }
    return [...grupos.entries()];
  }, [productos]);

  if (!negocio) return null;
  if (productos === null) return <Esqueleto lineas={8} />;

  return (
    <div className="carta-codigos mx-auto max-w-5xl">
      <div className="no-imprimir mb-4 flex items-center justify-between gap-3">
        <Link to="/catalogo" className="text-info underline">← Catálogo</Link>
        <p className="text-muted text-contenido">Imprime en carta u oficio. Si la van a plastificar: laminado <strong>mate</strong> (el brillante refleja el lector).</p>
        <Boton onClick={() => window.print()}>Imprimir</Boton>
      </div>

      <header className="mb-3 flex items-baseline justify-between border-b-2 border-black pb-1">
        <h1 className="text-titulo font-extrabold">{negocio.negocioNombre} · Carta de códigos</h1>
        <span className="num text-contenido">v{formatearFecha(fechaNegocio())}</span>
      </header>

      {porCategoria.map(([categoria, lineas]) => (
        <section key={categoria} className="categoria-carta mb-4">
          <h2 className="bg-surface mb-2 px-2 py-1 text-contenido font-extrabold uppercase tracking-wide print:bg-transparent print:border-b print:border-black">
            {categoria}
          </h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {lineas.map((p) => (
              <div key={p.id} className="celda-codigo rounded-card border border-border p-2 text-center print:rounded-none print:border-black">
                <p className="truncate text-contenido font-bold leading-tight">{p.nombre}</p>
                <p className="num text-contenido font-extrabold">{formatearCOP(p.precio)}</p>
                <div className="flex justify-center overflow-hidden">
                  <CodigoBarras valor={p.sku} />
                </div>
                <p className="num text-etiqueta tracking-widest">{p.sku}</p>
              </div>
            ))}
          </div>
        </section>
      ))}
      <footer className="text-muted mt-4 text-center text-etiqueta print:text-black">Hecho con {instalacion.nombre}</footer>
    </div>
  );
}
